/**
 * Instrument and screener navigation fixtures.
 *
 * FIXTURE data: the codes and names are real IDX listings but the list is a
 * hand-picked subset, not a live /param/list/stock response. Swapping in the
 * live list means replacing this module — `searchInstruments` takes the list
 * as an argument precisely so nothing else changes.
 *
 * The screener slugs, by contrast, are the real `ScreenerList` values from
 * quantist_api, so the navigation already points at endpoints that exist.
 */
import type { InstrumentOption } from "../domain/instruments.ts";

export const INSTRUMENT_FIXTURE: readonly InstrumentOption[] = [
	{ code: "composite", name: "IHSG / Jakarta Composite Index", sector: "Index" },
	{ code: "bbca", name: "Bank Central Asia", sector: "Financials" },
	{ code: "bbri", name: "Bank Rakyat Indonesia", sector: "Financials" },
	{ code: "bmri", name: "Bank Mandiri", sector: "Financials" },
	{ code: "bbni", name: "Bank Negara Indonesia", sector: "Financials" },
	{ code: "tlkm", name: "Telkom Indonesia", sector: "Infrastructure" },
	{ code: "asii", name: "Astra International", sector: "Industrials" },
	{ code: "unvr", name: "Unilever Indonesia", sector: "Consumer staples" },
	{ code: "icbp", name: "Indofood CBP Sukses Makmur", sector: "Consumer staples" },
	{ code: "indf", name: "Indofood Sukses Makmur", sector: "Consumer staples" },
	{ code: "adro", name: "Alamtri Resources Indonesia", sector: "Energy" },
	{ code: "ptba", name: "Bukit Asam", sector: "Energy" },
	{ code: "antm", name: "Aneka Tambang", sector: "Basic materials" },
	{ code: "inco", name: "Vale Indonesia", sector: "Basic materials" },
	{ code: "mdka", name: "Merdeka Copper Gold", sector: "Basic materials" },
	{ code: "amrt", name: "Sumber Alfaria Trijaya", sector: "Consumer cyclicals" },
	{ code: "cpin", name: "Charoen Pokphand Indonesia", sector: "Consumer staples" },
	{ code: "klbf", name: "Kalbe Farma", sector: "Healthcare" },
	{ code: "smgr", name: "Semen Indonesia", sector: "Basic materials" },
	{ code: "untr", name: "United Tractors", sector: "Industrials" },
	{ code: "goto", name: "GoTo Gojek Tokopedia", sector: "Technology" },
	{ code: "brms", name: "Bumi Resources Minerals", sector: "Basic materials" },
	{ code: "excl", name: "XLSmart Telecom Sejahtera", sector: "Infrastructure" },
	{ code: "jsmr", name: "Jasa Marga", sector: "Infrastructure" },
	{ code: "pgas", name: "Perusahaan Gas Negara", sector: "Energy" },
].map((entry, position) => ({
	...entry,
	code: entry.code.toLowerCase(),
	// The offline list has no database index, so it carries its literal order.
	// Live rows keep the list route's own index instead.
	index: position + 1,
}));

export interface ScreenerEntry {
	/** The backend's ScreenerList value. */
	slug: string;
	label: string;
	hint: string;
	/** Free-form: the live catalogue supplies it, so a new group must not break typing. */
	group: string;
	/** False until a typed web-api result endpoint exists. Never fake results. */
	resultsAvailable?: boolean;
}

/**
 * One-line descriptions of each criterion.
 *
 * Product copy, not data — the API returns slug, label and group, and there
 * is no reason for a database to own an English sentence. Keyed by slug so
 * the live catalogue can pick them up; an unknown slug simply has no hint.
 */
export const SCREENER_HINTS: Readonly<Record<string, string>> = {
	most_accumulated: "Largest positive whale flow",
	most_distributed: "Largest negative whale flow",
	vwap_rally: "Close never dipped below VWAP",
	vwap_around: "Close sitting on VWAP",
	vwap_breakout: "Crossed up through VWAP",
	vwap_breakdown: "Crossed down through VWAP",
	vprofile_inside: "Working a net-value node",
	vprofile_breakout: "Closed above resistance node",
	vprofile_breakdown: "Closed below support node",
	vprofile_support_bounce: "Rejected upward off support",
	vprofile_resistance_rejection: "Rejected downward off resistance",
};

/** Offline fallback: the same slugs and groups the live catalogue returns. */
export const SCREENERS: readonly ScreenerEntry[] = [
	{ slug: "most_accumulated", label: "Most accumulated", group: "Money flow" },
	{ slug: "most_distributed", label: "Most distributed", group: "Money flow" },
	{ slug: "vwap_rally", label: "Rally", group: "VWAP" },
	{ slug: "vwap_around", label: "Around VWAP", group: "VWAP" },
	{ slug: "vwap_breakout", label: "Breakout", group: "VWAP" },
	{ slug: "vwap_breakdown", label: "Breakdown", group: "VWAP" },
	{ slug: "vprofile_inside", label: "Inside zone", group: "Volume profile" },
	{ slug: "vprofile_breakout", label: "Zone breakout", group: "Volume profile" },
	{ slug: "vprofile_breakdown", label: "Zone breakdown", group: "Volume profile" },
	{ slug: "vprofile_support_bounce", label: "Support bounce", group: "Volume profile" },
	{ slug: "vprofile_resistance_rejection", label: "Resistance rejection", group: "Volume profile" },
].map((entry) => ({ ...entry, hint: SCREENER_HINTS[entry.slug] ?? "", resultsAvailable: false }));

/** Seeded recents so the sidebar is not empty on a first visit. */
export const RECENT_FIXTURE: readonly string[] = ["composite", "bbri", "antm", "tlkm"];
